import { JsonEditorField } from "./json-editor-field";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Switch } from "@/app/shared/ui/switch";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import { Textarea } from "@groot/ui/textarea";
import { type Account, AccountType, BankSyncProvider } from "@/types";
import { ArrowLeft, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface FormData {
  accountName: string;
  type: AccountType;
  accountNumber: string;
  lastFourAccountNumber: string;
  previousLastFourAccountNumber: string; // Comma separated
  currentBalance: string;
  creditLimit: string;
  availableCreditLimit: string;
  currencyCode: string;
  slug: string;
  iconUrl: string;
  notes: string;
  interestRate: string;
  termInMonths: string;
  statementDayOfMonth: string;
  paymentDueDayOfMonth: string;
  sortOrder: string;
  bankSyncEnabled: boolean;
  bankSyncProvider: string;
  bankSyncConfig: string; // JSON string
  meta: string; // JSON string
}

export function AccountForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    accountName: "",
    type: AccountType.BANK,
    accountNumber: "",
    lastFourAccountNumber: "",
    previousLastFourAccountNumber: "",
    currentBalance: "0",
    creditLimit: "",
    availableCreditLimit: "",
    currencyCode: "INR",
    slug: "",
    iconUrl: "",
    notes: "",
    interestRate: "",
    termInMonths: "",
    statementDayOfMonth: "",
    paymentDueDayOfMonth: "",
    sortOrder: "",
    bankSyncEnabled: false,
    bankSyncProvider: "",
    bankSyncConfig: "{}",
    meta: "{}",
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadAccount changes on every render
  useEffect(() => {
    if (isEditMode) {
      loadAccount();
    }
  }, [id, isEditMode]);

  const loadAccount = async () => {
    try {
      setLoading(true);
      const account = await apiClient.getAccount(Number(id));
      setFormData({
        accountName: account.accountName,
        type: account.type,
        accountNumber: account.accountNumber || "",
        lastFourAccountNumber: account.lastFourAccountNumber || "",
        previousLastFourAccountNumber: (account.previousLastFourAccountNumber || []).join(", "),
        currentBalance: account.currentBalance?.toString() || "0",
        creditLimit: account.creditLimit?.toString() || "",
        availableCreditLimit: account.availableCreditLimit?.toString() || "",
        currencyCode: account.currencyCode || "INR",
        slug: account.slug,
        iconUrl: account.iconUrl || "",
        notes: account.notes || "",
        interestRate: account.interestRate?.toString() || "",
        termInMonths: account.termInMonths?.toString() || "",
        statementDayOfMonth: account.statementDayOfMonth?.toString() || "",
        paymentDueDayOfMonth: account.paymentDueDayOfMonth?.toString() || "",
        sortOrder: account.sortOrder?.toString() || "",
        bankSyncEnabled: account.bankSyncEnabled || false,
        bankSyncProvider: account.bankSyncProvider || "",
        bankSyncConfig: account.bankSyncConfig
          ? JSON.stringify(account.bankSyncConfig, null, 2)
          : "{}",
        meta: account.meta ? JSON.stringify(account.meta, null, 2) : "{}",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load account",
      });
      navigate("/accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate slug from account name
    if (field === "accountName" && !isEditMode) {
      const slug = String(value)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.accountName.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Account name is required",
      });
      return;
    }

    if (!formData.slug.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Slug is required",
      });
      return;
    }

    if (formData.bankSyncEnabled && !formData.bankSyncProvider) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Bank Sync Provider is required when Bank Sync is enabled",
      });
      return;
    }

    let parsedMeta = {};
    try {
      parsedMeta = JSON.parse(formData.meta);
    } catch {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Invalid JSON in Meta field",
      });
      return;
    }

    let parsedBankSyncConfig = {};
    if (formData.bankSyncEnabled) {
      try {
        parsedBankSyncConfig = JSON.parse(formData.bankSyncConfig);
      } catch {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Invalid JSON in Bank Sync Config field",
        });
        return;
      }
    }

    try {
      setSaving(true);
      const payload: Partial<Account> = {
        accountName: formData.accountName.trim(),
        type: formData.type,
        slug: formData.slug.trim(),
        currencyCode: formData.currencyCode,
        currentBalance: formData.currentBalance ? Number.parseFloat(formData.currentBalance) : 0,
        accountNumber: formData.accountNumber || undefined,
        lastFourAccountNumber: formData.lastFourAccountNumber || undefined,
        previousLastFourAccountNumber: formData.previousLastFourAccountNumber
          ? formData.previousLastFourAccountNumber
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        creditLimit: formData.creditLimit ? Number.parseFloat(formData.creditLimit) : undefined,
        availableCreditLimit: formData.availableCreditLimit
          ? Number.parseFloat(formData.availableCreditLimit)
          : undefined,
        iconUrl: formData.iconUrl || undefined,
        notes: formData.notes || undefined,
        interestRate: formData.interestRate ? Number.parseFloat(formData.interestRate) : undefined,
        termInMonths: formData.termInMonths ? Number.parseInt(formData.termInMonths) : undefined,
        sortOrder: formData.sortOrder ? Number.parseInt(formData.sortOrder) : undefined,
        statementDayOfMonth: formData.statementDayOfMonth
          ? Number.parseInt(formData.statementDayOfMonth)
          : undefined,
        paymentDueDayOfMonth: formData.paymentDueDayOfMonth
          ? Number.parseInt(formData.paymentDueDayOfMonth)
          : undefined,
        bankSyncEnabled: formData.bankSyncEnabled,
        bankSyncProvider: formData.bankSyncEnabled
          ? (formData.bankSyncProvider as BankSyncProvider)
          : undefined,
        bankSyncConfig: formData.bankSyncEnabled ? parsedBankSyncConfig : undefined,
        meta: parsedMeta,
      };

      if (isEditMode) {
        await apiClient.updateAccount(Number(id), payload);
        toast({
          title: "Success",
          description: "Account updated successfully",
        });
      } else {
        await apiClient.createAccount(payload);
        toast({
          title: "Success",
          description: "Account created successfully",
        });
      }

      navigate("/accounts");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${isEditMode ? "update" : "create"} account`,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page maxWidth="3xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page maxWidth="3xl">
      {/* Header */}
      <Button variant="ghost" onClick={() => navigate("/accounts")} className="mb-4 -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to accounts
      </Button>
      <PageHeader
        title={isEditMode ? "Edit account" : "New account"}
        description={isEditMode ? "Update account details" : "Create a new financial account"}
        className="mb-8"
      />

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="border border-border rounded-lg p-6">
          <h2 className="text-sm font-medium text-foreground mb-4">Basic information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="accountName">Account name *</Label>
              <Input
                id="accountName"
                value={formData.accountName}
                onChange={(e) => handleChange("accountName", e.target.value)}
                placeholder="e.g., HDFC Savings Account"
                className="border-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Account type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleChange("type", value as AccountType)}
              >
                <SelectTrigger id="type" className="border-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AccountType.BANK}>Bank</SelectItem>
                  <SelectItem value={AccountType.CREDIT_CARD}>Credit Card</SelectItem>
                  <SelectItem value={AccountType.CASH}>Cash</SelectItem>
                  <SelectItem value={AccountType.LOAN}>Loan</SelectItem>
                  <SelectItem value={AccountType.DIGITAL_WALLET}>Digital Wallet</SelectItem>
                  <SelectItem value={AccountType.CRYPTOCURRENCY}>Cryptocurrency</SelectItem>
                  <SelectItem value={AccountType.OTHER}>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => handleChange("slug", e.target.value)}
                placeholder="e.g., hdfc-savings"
                className="border-input"
                pattern="[a-z0-9\-]+"
                required
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, and hyphens only
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="iconUrl">Icon URL</Label>
              <Input
                id="iconUrl"
                type="url"
                value={formData.iconUrl}
                onChange={(e) => handleChange("iconUrl", e.target.value)}
                placeholder="https://example.com/icon.png"
                className="border-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currencyCode">Currency</Label>
              <Input
                id="currencyCode"
                value={formData.currencyCode}
                onChange={(e) => handleChange("currencyCode", e.target.value)}
                placeholder="INR"
                className="border-input"
                maxLength={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">Sort Order</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => handleChange("sortOrder", e.target.value)}
                placeholder="e.g., 1"
                className="border-input"
              />
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Optional notes about this account"
              className="border-input"
              rows={3}
            />
          </div>
        </div>

        <div className="border border-border rounded-lg p-6">
          <h2 className="text-sm font-medium text-foreground mb-4">Account Identifiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="accountNumber">Account Number</Label>
              <Input
                id="accountNumber"
                value={formData.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
                placeholder="Full account number"
                className="border-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastFourAccountNumber">Last 4 digits</Label>
              <Input
                id="lastFourAccountNumber"
                value={formData.lastFourAccountNumber}
                onChange={(e) => handleChange("lastFourAccountNumber", e.target.value)}
                placeholder="1234"
                className="border-input"
                maxLength={4}
                pattern="[0-9]{4}"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="previousLastFourAccountNumber">
                Previous Last 4 Digits (comma separated)
              </Label>
              <Input
                id="previousLastFourAccountNumber"
                value={formData.previousLastFourAccountNumber}
                onChange={(e) => handleChange("previousLastFourAccountNumber", e.target.value)}
                placeholder="1234, 5678"
                className="border-input"
              />
            </div>
          </div>
        </div>

        <div className="border border-border rounded-lg p-6">
          <h2 className="text-sm font-medium text-foreground mb-4">Financial details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="currentBalance">Current balance</Label>
              <Input
                id="currentBalance"
                type="number"
                step="0.01"
                value={formData.currentBalance}
                onChange={(e) => handleChange("currentBalance", e.target.value)}
                placeholder="0.00"
                className="border-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="statementDayOfMonth">Statement Day</Label>
              <Input
                id="statementDayOfMonth"
                type="number"
                min="1"
                max="31"
                value={formData.statementDayOfMonth}
                onChange={(e) => handleChange("statementDayOfMonth", e.target.value)}
                placeholder="e.g. 15"
                className="border-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentDueDayOfMonth">Payment Due Day</Label>
              <Input
                id="paymentDueDayOfMonth"
                type="number"
                min="1"
                max="31"
                value={formData.paymentDueDayOfMonth}
                onChange={(e) => handleChange("paymentDueDayOfMonth", e.target.value)}
                placeholder="e.g. 5"
                className="border-input"
              />
            </div>

            {formData.type === AccountType.CREDIT_CARD && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="creditLimit">Credit limit</Label>
                  <Input
                    id="creditLimit"
                    type="number"
                    step="0.01"
                    value={formData.creditLimit}
                    onChange={(e) => handleChange("creditLimit", e.target.value)}
                    placeholder="0.00"
                    className="border-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availableCreditLimit">Available credit</Label>
                  <Input
                    id="availableCreditLimit"
                    type="number"
                    step="0.01"
                    value={formData.availableCreditLimit}
                    onChange={(e) => handleChange("availableCreditLimit", e.target.value)}
                    placeholder="0.00"
                    className="border-input"
                  />
                </div>
              </>
            )}

            {formData.type === AccountType.LOAN && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="interestRate">Interest rate (%)</Label>
                  <Input
                    id="interestRate"
                    type="number"
                    step="0.01"
                    value={formData.interestRate}
                    onChange={(e) => handleChange("interestRate", e.target.value)}
                    placeholder="0.00"
                    className="border-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="termInMonths">Term (months)</Label>
                  <Input
                    id="termInMonths"
                    type="number"
                    value={formData.termInMonths}
                    onChange={(e) => handleChange("termInMonths", e.target.value)}
                    placeholder="12"
                    className="border-input"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg p-6">
          <h2 className="text-sm font-medium text-foreground mb-4">Bank Sync Configuration</h2>
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="bankSyncEnabled"
                checked={formData.bankSyncEnabled}
                onCheckedChange={(checked) => handleChange("bankSyncEnabled", checked)}
              />
              <Label htmlFor="bankSyncEnabled">Enable Bank Sync</Label>
            </div>

            {formData.bankSyncEnabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bankSyncProvider">Provider</Label>
                  <Select
                    value={formData.bankSyncProvider}
                    onValueChange={(value) => handleChange("bankSyncProvider", value)}
                  >
                    <SelectTrigger id="bankSyncProvider" className="border-input">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={BankSyncProvider.FOLD_MONEY}>Fold Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <JsonEditorField
                  id="bankSyncConfig"
                  label="Configuration (JSON)"
                  value={formData.bankSyncConfig}
                  onChange={(value) => handleChange("bankSyncConfig", value)}
                  helperText="Provide provider-specific settings as valid JSON."
                />
              </>
            )}
          </div>
        </div>

        <div className="border border-border rounded-lg p-6">
          <h2 className="text-sm font-medium text-foreground mb-4">Advanced</h2>
          <JsonEditorField
            id="meta"
            label="Meta Data (JSON)"
            value={formData.meta}
            onChange={(value) => handleChange("meta", value)}
            helperText="Store additional account metadata as JSON."
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={() => navigate("/accounts")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <div className="size-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditMode ? "Update account" : "Create account"}
              </>
            )}
          </Button>
        </div>
      </form>
    </Page>
  );
}
